// ** react imports
import { useState } from 'react';

// ** router imports
import { Link, useNavigate } from 'react-router-dom';

// ** redux imports
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsOTPVerification,
  setToken,
  setUserDetails,
} from '../../../redux/slices/users';

// ** otp imports
import OtpInput from 'react-otp-input';

// ** hooks
import { useVerifyOtp, useResendOtp } from '../../../hooks/apis/useUsers';

// ** context imports
import { useThemeProvider } from '../../../contexts/ThemeContext';

// ** toaster imports
import { toast } from 'react-toastify';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES } from '../../../enums/common';

// ** images imports
import Logo from '../../../images/Logo.png';

const VerifyOtpForm = props => {
  // props
  const { data, isLoginVerification } = props;

  // states
  const [otp, setOtp] = useState();

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentTheme } = useThemeProvider();
  const { forgotPasswordEmail } = useSelector(state => state.users);

  // custom hooks
  const { isVerifyOtpLoading, verifyOtp } = useVerifyOtp();
  const { isResendOtpLoading, resendOtp } = useResendOtp();

  // form submit handler
  const onSubmit = async e => {
    e.preventDefault();
    if (!otp) {
      return toast.error('OTP must required.');
    }
    if (otp.length < 6) {
      return toast.error('OTP must be 6 digits.');
    }

    const payload = {
      otp: Number(otp),
      email: isLoginVerification ? data.email : forgotPasswordEmail,
    };
    const response = await verifyOtp(payload);
    if (response.data.success) {
      if (isLoginVerification) {
        const { token, user } = response.data.data || {};

        if (token) dispatch(setToken(token));
        if (user) dispatch(setUserDetails(user));

        const role = user?.role;
        if (role === USER_ROLES.ADMIN) {
          navigate('/users/list');
        } else if (role === USER_ROLES.MANAGER) {
          navigate('/customers/list');
        } else {
          navigate('/');
        }
      } else {
        dispatch(setIsOTPVerification(true));
      }
    }
  };

  const handleResendOtp = async () => {
    await resendOtp({
      email: forgotPasswordEmail,
    });
  };

  return (
    <div className="md:w-1/2">
      <div className="min-h-dvh h-full flex flex-col after:flex-1">
        {/* Header */}
        <div className="flex-1">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <Link className="block" to="/">
              <div className="w-10 h-10">
                <img
                  src={Logo}
                  alt="Multi Tax"
                  className="h-full w-full object-contain"
                />
              </div>
            </Link>
          </div>
        </div>

        <div className="max-w-sm mx-auto w-full px-4 py-8">
          <h1 className="text-3xl text-gray-800 dark:text-gray-100 font-bold mb-2">
            OTP Verification
          </h1>
          {isLoginVerification ? (
            <p className="text-sm text-gray-800 dark:text-gray-100 font-ligh mb-6">
              A verification code was sent to the admin&apos;s phone number.
            </p>
          ) : (
            <p className="text-sm text-gray-800 dark:text-gray-100 font-ligh mb-6">
              We will send you a{' '}
              <span className="font-bold">One Time Password</span> on your this{' '}
              <span className="font-bold">{forgotPasswordEmail}</span> email
              address.
            </p>
          )}

          {/* Form */}
          <form onSubmit={e => onSubmit(e)}>
            <div className="space-y-4">
              <OtpInput
                containerStyle={{
                  width: '100%',
                }}
                inputStyle={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                  backgroundColor:
                    currentTheme === 'dark' ? '#0b1d2c' : '#ffffff',
                  borderRadius: '5px',
                  border: '1px solid #e5e7eb',
                  '&:focus': {
                    boxShadow: 'none',
                    outline: 'none',
                  },
                }}
                value={otp}
                onChange={setOtp}
                numInputs={6}
                inputType="tel"
                renderSeparator={<span style={{ margin: '6px' }}>-</span>}
                renderInput={props => <input {...props} />}
              />
            </div>

            <div className="flex justify-between items-center mt-6">
              {!isLoginVerification ? (
                <div className="" onClick={handleResendOtp}>
                  <span className="text-sm text-gray-800 dark:text-gray-100 cursor-pointer hover:underline">
                    Resend OTP
                  </span>
                </div>
              ) : (
                <div className=""></div>
              )}

              <LoadingButton
                type="submit"
                theme={BUTTON_THEMES.PRIMARY}
                isLoading={isVerifyOtpLoading}
              >
                Continue
              </LoadingButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtpForm;
